// Utility functions for map operations

export const createColorScale = (visParams: any) => {
  if (!visParams || !visParams.palette) return [];
  
  const { min, max, palette } = visParams;
  const steps = palette.length;
  const stepSize = (max - min) / (steps - 1);
  
  return palette.map((color: string, index: number) => ({
    color,
    value: min + (stepSize * index),
    label: (min + (stepSize * index)).toFixed(3)
  }));
};

export const formatCoordinates = (coordinates: [number, number]): string => {
  return `${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}`;
};

export const formatDate = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options
  });
};

export const calculateProgress = (current: number, total: number): number => {
  return Math.round(((current + 1) / total) * 100);
};

// Analysis info helper
export const getCurrentAnalysisInfo = (
  showTimeSeriesSlider: boolean,
  currentTimeSeriesImage: any,
  timeSeriesData: any,
  indexHeatMapData: any
) => {
  if (showTimeSeriesSlider && currentTimeSeriesImage) {
    return {
      type: 'Time Series',
      index: timeSeriesData?.index?.toUpperCase() || '',
      date: formatDate(currentTimeSeriesImage.date),
      value: currentTimeSeriesImage.mean_index_value?.toFixed(3),
      spatialResolution: null as string | null,
      spatialResolutionNote: null as string | null,
    };
  } else if (indexHeatMapData) {
    // Check if it's burn detection data
    if (indexHeatMapData.type === 'burndetection') {
      return {
        type: 'Burn Detection',
        index: 'BURN DETECTION',
        date: indexHeatMapData.period || '',
        value: `${indexHeatMapData.statistics?.burned_percentage?.toFixed(2)}% Burned`,
        spatialResolution: null as string | null,
        spatialResolutionNote: null as string | null,
      };
    }
    return {
      type: 'Index Map',
      index: indexHeatMapData.index?.toUpperCase() || '',
      date: formatDate(indexHeatMapData.firstImageDate || indexHeatMapData.observationDate),
      value: null,
      spatialResolution: indexHeatMapData.spatialResolution ?? null,
      spatialResolutionNote: indexHeatMapData.spatialResolutionNote ?? null,
      bandResolutions: indexHeatMapData.bandResolutions ?? null,
      satellite: indexHeatMapData.satellite ?? null,
    };
  }
  return null;
};

// Get visualization parameters
export const getVisualizationParams = (timeSeriesData: any, indexHeatMapData: any) => {
  return timeSeriesData?.vis_params || indexHeatMapData?.visParams || null;
};

// Debounce function for search
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
};